import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  TextField,
  Typography,
  Chip,
  InputAdornment,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { AddRounded } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { toast } from "react-toastify";
import axios from "axios";
import { useDialog } from "../../../DialogContext";
import {
  getActivities,
  getCategories,
  updateActivity,
} from "../../../../redux/Apps/Lawyerapp/ActivitySlice/ActivitySlice";
import { backend_api } from "../../../../static/server";
import {
  ListboxComponent,
  NoDataOptionField,
} from "../../../../static/staticdata";
import ExpCategoriForm from "./ExpCategoriForm";
import noOption from "../../../../assets/no-option-found.webp";
import DrawerTemplate from "../../Cases/DrawerTemplate";
import MatterApiField from "../../Settings/FieldsUi/MatterApiField";
import { getCurrencySymbol } from "../../../../pages/LawyerPages/Settings/CurrencySetForm";
import { isDevMode } from "../../../Auth/SubscriptionContext";
import {
  resolveMatterCurrency,
  resolveMatterHourlyRate,
} from "../../../../utils/matterBilling";

const ExpenseForm = ({ entryData, editMode = false, onSuccess }) => {
  const dispatch = useDispatch();
  const { showDialog, closeDialog } = useDialog();
  const [isSubmiting, setIsSubmiting] = useState(false);
  const [isEditMode] = useState(editMode);
  const [selectedMatterCurrency, setSelectedMatterCurrency] = useState(null);
  const [currencySymbol, setCurrencySymbol] = useState("$");

  const {
    token,
    defaultCurrency,
  } = useSelector((data) => data.user);
  const { allCategories, loading } = useSelector((state) => state.activity);

  useEffect(() => {
    dispatch(
      getCategories({
        url: `${backend_api}firm-case/expense-category/`,
        token: token,
      })
    );
  }, [dispatch, token]);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      type: "expense",
      case: null,
      category: null,
      quantity: 1,
      hourly_rate: 0,
      description: "",
      non_billable: false,
      bill_entry_show: false,
      currency: defaultCurrency || "USD",
    },
  });

  const quantity = watch("quantity") || 0;
  const price = watch("hourly_rate") || 0;
  const non_billable = watch("non_billable");
  const formCurrency = watch("currency");
  const selectedCase = watch("case");
  const selectedCategory = watch("category");
  const description = watch("description");

  // Update currency symbol when formCurrency changes
  useEffect(() => {
    const symbol = getCurrencySymbol(formCurrency);
    setCurrencySymbol(symbol);
  }, [formCurrency]);

  const fetchMatterDetails = async (caseId) => {
    if (!caseId) return null;

    try {
      const response = await axios.get(
        `${backend_api}case/register/${caseId}/`,
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        }
      );

      return response.data || null;
    } catch (error) {
      console.error("Error fetching matter details:", error);
      return null;
    }
  };

  // Handle matter selection
  const handleMatterChange = async (selectedValue) => {

    if (!selectedValue) {
      setValue("case", null);
      setValue("currency", defaultCurrency || "USD");
      setValue("hourly_rate", 0);
      setSelectedMatterCurrency(null);
      return;
    }

    const matterId =
      typeof selectedValue === "object" && selectedValue?.id
        ? selectedValue.id
        : selectedValue;

    setValue("case", matterId);

    let matterData =
      typeof selectedValue === "object" && selectedValue?.id
        ? selectedValue
        : null;

    const localCurrency = resolveMatterCurrency(matterData, "");
    const localHourlyRate = resolveMatterHourlyRate(matterData);

    if ((!localCurrency || !localHourlyRate) && matterId) {
      const fetchedMatter = await fetchMatterDetails(matterId);
      if (fetchedMatter) {
        matterData = fetchedMatter;
      }
    }

    const resolvedMatterCurrency = resolveMatterCurrency(matterData, "");
    const resolvedHourlyRate = resolveMatterHourlyRate(matterData);

    if (resolvedMatterCurrency) {
      setValue("currency", resolvedMatterCurrency, { shouldValidate: true });
      setSelectedMatterCurrency(resolvedMatterCurrency);
    } else {
      setValue("currency", defaultCurrency || "USD", { shouldValidate: true });
      setSelectedMatterCurrency(null);
    }

    setValue("hourly_rate", resolvedHourlyRate || 0, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  // Set edit data
  useEffect(() => {
    if (entryData && isEditMode) {

      // Reset form first
      reset({
        type: "expense",
        case: entryData.case?.id || null,
        category: entryData.category?.id || null,
        quantity: entryData.quantity || 1,
        hourly_rate: entryData.hourly_rate || 0,
        description: entryData.description || "",
        non_billable: entryData.non_billable || false,
        bill_entry_show: entryData.bill_entry_show || false,
        currency: entryData.currency || defaultCurrency || "USD",
      });

      // If case has currency, set it
      if (entryData.case?.currency) {
        setSelectedMatterCurrency(entryData.case.currency);
      } else if (entryData.currency) {
        setSelectedMatterCurrency(entryData.currency);
      }
    }
  }, [entryData, isEditMode, reset, defaultCurrency]);

  const onSubmit = async (data) => {
    setIsSubmiting(true);
    try {
      let responseData = null;
      if (isEditMode) {
        responseData = await dispatch(
          updateActivity({
            url: `${backend_api}firm-case/expense/${entryData?.id}/`,
            data: data,
            token: token,
          })
        ).unwrap();
        toast.success("Expense entry updated successfully");
      } else {
        const response = await axios.post(
          `${backend_api}firm-case/expense/`,
          data,
          {
            headers: {
              Authorization: `Token ${token}`,
            },
          }
        );
        responseData = response.data;
        toast.success("Expense entry created successfully");
      }

      if (onSuccess && responseData) onSuccess(responseData);
    } catch (error) {
      toast.error(error.message || "Failed to create expense");
    } finally {
      setIsSubmiting(false);
      reset();
      setSelectedMatterCurrency(null);
      closeDialog();
      dispatch(
        getActivities({ url: `${backend_api}firm-case/expense/`, token: token })
      );
    }
  };

  // Calculate total amount
  const totalAmount = useMemo(() => {
    if (non_billable) return "0.00";
    return (parseFloat(quantity) * parseFloat(price)).toFixed(2);
  }, [quantity, price, non_billable]);

  // Form validation
  const isFormValid = useMemo(() => {
    return (
      !!selectedCase &&
      !!selectedCategory &&
      !!description &&
      parseFloat(price) >= 0
    );
  }, [selectedCase, selectedCategory, description, price]);

  return (
    <Box sx={{ position: "relative" }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            p: "1rem",
          }}
        >
          <Box>
            <Controller
              name="category"
              control={control}
              rules={{ required: "Category is required" }}
              render={({ field }) => (
                <FormControl size="small" fullWidth error={!!errors?.category}>
                  <Autocomplete
                    {...field}
                    options={allCategories || []}
                    loading={loading}
                    getOptionLabel={(option) => {
                      if (!option) return "";
                      return option.name || "";
                    }}
                    value={
                      field.value
                        ? allCategories?.find(
                            (item) => item.id === field.value
                          ) || null
                        : null
                    }
                    onChange={(_, newValue) =>
                      field.onChange(newValue?.id || null)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={
                          <span>
                            Category<span style={{ color: "red" }}>*</span>
                          </span>
                        }
                        size="small"
                        error={!!errors?.category}
                        helperText={errors?.category?.message}
                      />
                    )}
                    ListboxComponent={(props) => (
                      <ListboxComponent
                        {...props}
                        btnIcon={<AddRounded />}
                        btnText="Add category"
                        handleClikListBtn={() => {
                          showDialog(<ExpCategoriForm />, {
                            dialogTitle: "New Expense category",
                          });
                        }}
                      />
                    )}
                    noOptionsText={
                      <NoDataOptionField
                        imgSrc={noOption}
                        text="No category found"
                        btnText="Category"
                        handleClickNoData={() => {
                          showDialog(<ExpCategoriForm />, {
                            dialogTitle: "New Expense category",
                          });
                        }}
                      />
                    }
                    PopperProps={{
                      sx: { zIndex: 3000 },
                      container: document.body,
                    }}
                  />
                </FormControl>
              )}
            />
          </Box>

          <Box>
            <Controller
              name="case"
              control={control}
              rules={{ required: "Matter is required" }}
              render={({ field }) => (
                <MatterApiField
                  label="Matter"
                  value={field.value}
                  onChange={async (value) => {
                    await handleMatterChange(value);
                    // Update form field - when returnFullData=true, pass the ID
                    if (typeof value === "object" && value !== null) {
                      field.onChange(value.id);
                    } else {
                      field.onChange(value);
                    }
                  }}
                  errors={errors}
                  token={token}
                  apiUrl={`${backend_api}case/register/`}
                  isRequired={true}
                  AddMatterFunc={({ onCreated }) =>
                    showDialog(<DrawerTemplate onMatterCreated={onCreated} />, {
                      dialogTitle: "Select Template",
                    })
                  }
                  disabled={isEditMode}
                  returnFullData={true} // Get full matter data to access currency
                  editData={entryData?.case || null}
                />
              )}
            />
          </Box>

          {/* Display current currency */}
          {isDevMode && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Currency:
              </Typography>
              <Chip
                label={`${formCurrency} ${currencySymbol}`}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 500 }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ ml: 1 }}
              >
                {selectedMatterCurrency
                  ? `(Using matter's currency)`
                  : `(Using your default currency)`}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: "flex", gap: "1rem" }}>
            <Controller
              name="quantity"
              control={control}
              defaultValue={1.0}
              rules={{
                required: "Quantity is required",
                min: { value: 0, message: "Quantity must be positive" },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  size="small"
                  label="Quantity"
                  type="number"
                  inputProps={{ step: "0.01", min: "0" }}
                  fullWidth
                  error={!!errors?.quantity}
                  helperText={errors?.quantity?.message}
                />
              )}
            />
            <Controller
              name="hourly_rate"
              control={control}
              rules={{
                required: "Price is required",
                min: { value: 0, message: "Price must be positive" },
              }}
              render={({ field }) => (
                <TextField
                  size="small"
                  {...field}
                  label={
                    <span>
                      Price<span style={{ color: "red" }}>*</span>
                    </span>
                  }
                  type="number"
                  inputProps={{ step: "0.01", min: "0" }}
                  fullWidth
                  error={!!errors?.hourly_rate}
                  helperText={errors?.hourly_rate?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box
                          sx={{
                            px: 1,
                            py: 0.5,
                            borderRadius: "6px",
                            backgroundColor: "white",
                            fontSize: "0.875rem",
                            fontWeight: 500,
                            color: "text.secondary",
                          }}
                        >
                          {currencySymbol}
                        </Box>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              border: "1px solid #c5c5c7",
              borderRadius: "0.2rem",
              px: "0.5rem",
              py: "0.5rem",
              bgcolor: non_billable ? "#f5f5f5" : "#e3f2fd",
            }}
          >
            <Typography
              variant="body1"
              sx={{ fontSize: "0.9rem", fontWeight: "500" }}
            >
              Total Amount
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: "1rem",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontSize: "0.85rem",
                  color: non_billable ? "#9e9e9e" : "text.secondary",
                }}
              >
                {currencySymbol}
              </Typography>

              <Typography
                component="span"
                sx={{
                  fontSize: "1rem",
                  fontWeight: 700,
                  color: non_billable ? "#9e9e9e" : "primary.main",
                }}
              >
                {non_billable ? "0.00" : totalAmount}
              </Typography>
            </Typography>
          </Box>

          <Controller
            name="description"
            control={control}
            defaultValue=""
            rules={{ required: "Description is required" }}
            render={({ field }) => (
              <TextField
                size="small"
                {...field}
                label={
                  <span>
                    Description<span style={{ color: "red" }}>*</span>
                  </span>
                }
                multiline
                rows={3}
                fullWidth
                error={!!errors?.description}
                helperText={errors?.description?.message}
              />
            )}
          />

          <Box sx={{ display: "flex", gap: "1rem" }}>
            <Controller
              name="non_billable"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e.target.checked);
                        if (e.target.checked) {
                          setValue("bill_entry_show", false);
                        }
                      }}
                    />
                  }
                  label="Non-Billable"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontSize: "0.8rem",
                      fontWeight: 500,
                      color: "text.secondary",
                    },
                  }}
                />
              )}
            />
            <Controller
              name="bill_entry_show"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      disabled={!watch("non_billable")}
                    />
                  }
                  label="Show Entry on Bill"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontSize: "0.8rem",
                      fontWeight: 500,
                      color: "text.secondary",
                    },
                  }}
                />
              )}
            />
          </Box>
        </Box>

        <Box
          sx={{
            height: "fit-content",
            borderTop: "1px solid #ddd",
            position: "sticky",
            bottom: 0,
            backdropFilter: "blur(10px)",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            p: "0.5rem",
            zIndex: 1000,
            gap: "1rem",
          }}
        >
          <Button variant="outlined" size="small" onClick={closeDialog}>
            Cancel
          </Button>
          <LoadingButton
            loading={isSubmiting}
            variant="contained"
            color="primary"
            type="submit"
            size="small"
            disabled={!isFormValid}
          >
            {isEditMode ? "Update Expense" : "Save Expense"}
          </LoadingButton>
        </Box>
      </form>
    </Box>
  );
};

export default ExpenseForm;
